<template>
  <form
    class="form-work-mode"
    v-on:submit.prevent="onSubmit"
  >
    <fieldset class="form-work-mode__options" :disabled="!hasEditSchedule">
      <legend class="form-work-mode__legend">Выберите режим работы</legend>
      <div
        v-for="option in $options.optionList"
        v-bind:key="option.value"
      >
        <label class="form-work-mode__radio">
          <input
            name="work-mode"
            type="radio"
            v-bind:value="option.value"
            v-model="workMode" />
          <span>{{ option.title }}</span>
        </label>
        <weekdays-picker
          v-show="option.value === 'weekdays' && workMode === 'weekdays'"
          v-bind:hide-time="true"
          v-bind:initial-days="weekdays"
          v-on:change="onChangeWeekDay"
          v-on:input-date="onInputWeekdayStartsDate"
        />
      </div>
    </fieldset>
    <fieldset class="form-work-mode__options" :disabled="!hasEditSchedule">
      <legend class="form-work-mode__legend">Выберите период</legend>
      <label class="form-work-mode__radio">
        <input
          name="period-mode"
          type="radio"
          v-bind:value="$options.onlySelected"
          v-model="selectedPeriod" />
        <span>До конца месяца</span>
      </label>
      <label class="form-work-mode__radio">
        <input
          name="period-mode"
          type="radio"
          v-bind:value="$options.toEndSchedule"
          v-model="selectedPeriod" />
        <span>До конца расписания</span>
      </label>
    </fieldset>
    <div class="form-work-mode__buttons" v-if="hasEditSchedule">
      <button
        class="form-work-mode__cancel"
        type="button"
        v-on:click="onCancel"
      >Отмена</button>
      <button
        class="form-work-mode__submit"
        type="submit"
      >ОК</button>
    </div>
  </form>
</template>
<script>
import { builtInPopoverMixin } from '@/mixins/builtInPopoverMixin';
import WeekdaysPicker, { getWeekdays } from '@/components/WeekdaysPicker.vue';

/**
 * @description Компонент формы выбора режима работы.
 * @type {Vue}
 */
export default {
  components: { WeekdaysPicker },
  data() {
    const [
      weekdays,
    ] = this.prepareWorkModeOptions();
    return {
      /**
       * @description Дни недели.
       * @type {WeekDay[]}
       */
      weekdays,
      /**
       * @description Режим работы.
       * @type {string}
       */
      workMode: this.initialMode || this.$options.optionList[0].value,
      /**
       * @description Выбранный период.
       * @type {string}
       */
      selectedPeriod: this.$options.onlySelected,
    };
  },
  methods: {
    onCancel() {
      this.closePopover();
      this.workMode = this.initialMode || this.$options.optionList[0].value;
    },
    onChangeWeekDay(dayIndex, day) {
      this.$set(this.weekdays, dayIndex, day);
    },
    onInputWeekdayStartsDate(value) {
      this.weekdaysStartsDate = value;
    },
    onSubmit() {
      const params = [];
      if (this.workMode === 'weekdays') {
        const weekdays = this
          .weekdays
          .filter((candidate) => candidate.isChecked)
          .map((candidate) => ({
            value: candidate.value,
            work_times: candidate
              .workTimes
              .map((workTimes) => ({ starts_time: workTimes.from, ends_time: workTimes.to })),
          }));
        params.push(weekdays);
      }
      const onlySelected = this.selectedPeriod === this.$options.onlySelected;
      this.$emit('submit', this.workMode, onlySelected, this.closePopover, ...params);
    },
    prepareWorkModeOptions() {
      const weekdays = getWeekdays(this.initialWeekdays, this.initialFrom, this.initialTo);
      return [weekdays];
    },
  },
  props: {
    /**
     * @description Может редактировать график работы.
     * @type {boolean}
     */
    hasEditSchedule: { required: true, type: Boolean },
    initialMode: { type: String, required: true },
    initialWeekdays: {
      type: Array,
      default: () => ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    },
  },
  mixins: [
    builtInPopoverMixin,
  ],
  watch: {
    initialMode(value) {
      this.workMode = value;
    },
    initialWeekdays() {
      [this.weekdays] = this.prepareWorkModeOptions();
    },
  },
  name: 'FormWorkMode',
  onlySelected: 'only-selected',
  toEndSchedule: 'to-end-schedule',
  optionList: [
    {
      title: 'Пятидневка',
      value: 'five-days',
    },
    {
      title: 'Шестидневка',
      value: 'six-days',
    },
    {
      title: 'Ежедневно',
      value: 'seven-days',
    },
    {
      title: 'По дням недели',
      value: 'weekdays',
    },
  ],
};
</script>
<style lang="scss" scoped>
@use "@/scss/buttons";
$button-font-size: 14px;
$button-height: 37px;
$button-width: 100%;
.form-work-mode {
  padding: 22px 15px;
  width: 360px;
  max-width: 99%;
  &__legend {
    display: block;
    width: 100%;
    font-weight: 600;
  }
  &__options {
    border: none;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  &__radio>input {
    position: absolute;
    z-index: -1;
    opacity: 0;
  }
  &__radio>span {
    display: inline-flex;
    align-items: center;
    user-select: none;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    cursor: pointer;
  }
  &__radio>span::before {
    content: '';
    display: inline-block;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    flex-grow: 0;
    border: 1px solid #90A0B7;
    border-radius: 50%;
    margin-right: 10px;
    background-repeat: no-repeat;
    background-position: center center;
    background-size: 50% 50%;
  }
  &__radio>input:not(:disabled):not(:checked)+span:hover::before {
    border: 1px solid #0489E0;
  }
  &__radio>input:checked+span::before {
    border: 1px solid #0489E0;
    background-color: #0489E0;
    box-shadow: inset 0 0 0 6px #fff;
  }
  &__buttons {
    display: grid;
    grid-template-columns: 123px 123px;
    grid-gap: 6px;
    justify-content: flex-end;
    margin: 10px 0 0 0;
  }
  &__cancel {
    @include buttons.btn-transparent($button-width, $button-height, $button-font-size);
  }
  &__submit {
    @include buttons.btn-blue($button-width, $button-height, $button-font-size);
  }
}
</style>
